// Shared "populate the other coordinate fields from this one" logic for the four
// inter-convertible location fields (what3words, OS grid reference, postcode,
// lat/long) — used by both the Incidents form and the Event Locations dialog.
//
// Grid ref <-> lat/long is pure maths (geo.jsx). Postcode uses postcodes.io.
// what3words has no free API, so our backend proxies a public converter; that
// backend hop is invisible to the user — it looks like every other conversion.
//
// Conversions preserve precision: a grid ref's figure count and a lat/long's
// decimal places both encode how precise a location is, so we map between
// comparable levels rather than inventing digits. what3words identifies a ~3 m
// square (10-figure grid ref / 5-dp lat/long); a postcode is a ~100 m locator
// (6-figure / 3-dp).
import { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "./config";
import { notify } from "./notify";
import {
  parseLatLng, normalizeLatLng, latLngToOsGridRef, osGridRefToLatLng,
  parseGridRefFromUrl, gridRefFigures, figuresToDecimals, latLngDecimals,
  decimalsToFigures,
} from "./geo";

const humanList = (xs) =>
  xs.length <= 1 ? (xs[0] || "")
    : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;

// --- The device's own position ----------------------------------------------
// One more source for the same four fields, from the browser's Geolocation API.

// A fresh fix, never a cached one: an incident is where the responder is standing
// now, not where their phone last happened to know about. High accuracy asks for
// GNSS rather than a wifi/IP estimate, which is the whole point on a hillside.
const devicePosition = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error("This browser can't report your location."));
    return;
  }
  // Geolocation is refused outright on plain http (bar localhost), and browsers
  // report that as a plain permission denial — so say what it actually is.
  if (typeof window !== "undefined" && window.isSecureContext === false) {
    reject(new Error("Your browser only shares location over a secure (https) connection."));
    return;
  }
  navigator.geolocation.getCurrentPosition(resolve, reject, {
    enableHighAccuracy: true, timeout: 20000, maximumAge: 0,
  });
});

const GEO_ERRORS = {
  1: "Location permission was refused. Allow it for this site in your browser or phone settings, then try again.",
  2: "Your device couldn't get a fix. Move somewhere with a clearer view of the sky and try again.",
  3: "Getting a fix took too long. Try again — outdoors if you can.",
};

const geoErrorMessage = (e) =>
  GEO_ERRORS[e?.code] || e?.message || "Couldn't read your location.";

// Never claim more precision than the fix has: pick the level whose square is at
// least as coarse as the reported accuracy radius. A phone's GNSS outdoors is
// typically good to 5–20 m, so this usually lands on 4 dp / 8 figures (~10 m); a
// wifi-derived fix reporting hundreds of metres is recorded as such.
const decimalsForAccuracy = (metres) => {
  const m = Number.isFinite(metres) ? metres : Infinity;
  if (m <= 2) return 5;    // ~1 m
  if (m <= 20) return 4;   // ~10 m
  if (m <= 200) return 3;  // ~100 m
  return 2;                // ~1 km
};

// Nearest postcode for a coordinate. Best-effort — a plain fetch() bypasses the
// shared-axios auth/error interceptors (this is a third-party API, not our own).
const nearestPostcode = async (lat, lng) => {
  try {
    const res = await fetch(`https://api.postcodes.io/postcodes?lon=${lng}&lat=${lat}&limit=1&radius=2000`);
    return (await res.json())?.result?.[0]?.postcode || null;
  } catch { return null; }
};

// what3words via our backend proxy. Best-effort; suppressErrorToast stops a failed
// lookup double-toasting — the caller summarises the outcome itself.
const latLngToW3w = async (lat, lng) => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/geo/latlng-to-w3w`,
      { params: { lat, lng }, suppressErrorToast: true });
    return data?.words || null;
  } catch { return null; }
};
const w3wToLatLng = async (words) => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/geo/w3w-to-latlng`,
      { params: { words }, suppressErrorToast: true });
    return Number.isFinite(data?.lat) && Number.isFinite(data?.lng) ? [data.lat, data.lng] : null;
  } catch { return null; }
};

// keys maps the four systems to this form's field names, e.g.
//   { w3w: "location_what3words", gr: "location_grid_ref", pc: "location_postcode", ll: "lat_long" }
// setForm must accept a functional update (prev => next).
export function useLocationConvert({ setForm, keys }) {
  const [busy, setBusy] = useState(null); // which conversion is running: a key of `keys` or "link", else null
  const [grUrl, setGrUrl] = useState(""); // Grid Reference Finder link to populate all four from

  const fmtLatLng = (lat, lng, dp) => `${lat.toFixed(dp)}, ${lng.toFixed(dp)}`;

  // Fill every location field from a resolved coordinate, except those in `skip`.
  // `preset` seeds fields the caller already knows exactly (so we don't recompute
  // and drift, e.g. the source field or a grid ref taken straight from a link).
  const spread = async ({ skip, lat, lng, dp, figures, busyKey, source, preset }) => {
    setBusy(busyKey);
    const filled = [], missing = [];
    const sync = { ...(preset || {}) };
    if (!skip.includes("ll")) { sync[keys.ll] = fmtLatLng(lat, lng, dp); filled.push("lat/long"); }
    if (!skip.includes("gr")) {
      const gr = latLngToOsGridRef(lat, lng, figures);
      if (gr) { sync[keys.gr] = gr; filled.push("grid reference"); }
      else missing.push("grid reference (outside the National Grid)");
    }
    setForm(f => ({ ...f, ...sync }));

    const [pc, w3w] = await Promise.all([
      skip.includes("pc") ? null : nearestPostcode(lat, lng),
      skip.includes("w3w") ? null : latLngToW3w(lat, lng),
    ]);
    const later = {};
    if (!skip.includes("pc")) {
      if (pc) { later[keys.pc] = pc; filled.push("postcode"); }
      else missing.push("nearest postcode (none found)");
    }
    if (!skip.includes("w3w")) {
      if (w3w) { later[keys.w3w] = w3w; filled.push("what3words"); }
      else missing.push("what3words (lookup unavailable)");
    }
    if (Object.keys(later).length) setForm(f => ({ ...f, ...later }));
    setBusy(null);

    const msg = [
      filled.length ? `Populated ${humanList(filled)} from ${source}.` : "",
      missing.length ? `Couldn't fill ${humanList(missing)}.` : "",
    ].filter(Boolean).join(" ");
    notify(msg || "Nothing to populate.", !filled.length ? "error" : (missing.length ? "warning" : "success"));
  };

  const fromW3w = async (form) => {
    const words = (form[keys.w3w] || "").trim();
    if (!words) { notify("Enter a what3words address first.", "error"); return; }
    setBusy("w3w");
    const ll = await w3wToLatLng(words);
    if (!ll) { setBusy(null); notify(`Couldn't look up "${words}". Check the three words.`, "error"); return; }
    // what3words resolves to a ~3 m square: high precision (10-figure / 5-dp).
    await spread({ skip: ["w3w"], lat: ll[0], lng: ll[1], dp: 5, figures: 10, busyKey: "w3w", source: "what3words" });
  };

  const fromGridRef = async (form) => {
    const gr = (form[keys.gr] || "").trim();
    const ll = osGridRefToLatLng(gr);
    if (!ll) { notify("Enter a valid OS grid reference (e.g. SD5190993629) first.", "error"); return; }
    const figures = gridRefFigures(gr);
    await spread({ skip: ["gr"], lat: ll[0], lng: ll[1], dp: figuresToDecimals(figures), figures,
                   busyKey: "gr", source: "grid reference" });
  };

  const fromLatLng = async (form) => {
    const norm = normalizeLatLng(form[keys.ll]); // also converts pasted DMS -> decimal
    if (!norm) { notify('Enter a valid lat/long (e.g. "54.4512, -3.0125") first.', "error"); return; }
    const [lat, lng] = parseLatLng(norm);
    const dp = latLngDecimals(norm);
    await spread({ skip: ["ll"], lat, lng, dp, figures: decimalsToFigures(dp),
                   busyKey: "ll", source: "lat/long", preset: { [keys.ll]: norm } });
  };

  const fromPostcode = async (form) => {
    const pc = (form[keys.pc] || "").trim();
    if (!pc) { notify("Enter a postcode first.", "error"); return; }
    setBusy("pc");
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      const r = (await res.json())?.result;
      if (!r) { setBusy(null); notify(`Postcode "${pc}" not found.`, "error"); return; }
      await spread({ skip: ["pc"], lat: r.latitude, lng: r.longitude, dp: 3, figures: 6,
                     busyKey: "pc", source: "postcode" }); // postcode ≈ 100 m locator
    } catch { setBusy(null); notify("Postcode lookup failed.", "error"); }
  };

  // Populate all four fields from the device's own position. Unlike the other
  // sources this one starts from nothing the user has typed, so it fills lat/long
  // too — hence `skip: []`.
  const fromDevice = async () => {
    setBusy("device");  // set before the wait: a fix can take many seconds
    let pos;
    try {
      pos = await devicePosition();
    } catch (e) {
      setBusy(null);
      notify(geoErrorMessage(e), "error");
      return;
    }
    const { latitude, longitude, accuracy } = pos.coords || {};
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      setBusy(null);
      notify("Your device reported a position we couldn't read.", "error");
      return;
    }
    const dp = decimalsForAccuracy(accuracy);
    await spread({
      skip: [], lat: latitude, lng: longitude, dp, figures: decimalsToFigures(dp),
      busyKey: "device",
      source: Number.isFinite(accuracy)
        ? `this device (accurate to about ${Math.round(accuracy)} m)`
        : "this device",
    });
  };

  // Populate all four fields from a Grid Reference Finder link.
  const fromGrUrl = async () => {
    const gr = parseGridRefFromUrl(grUrl);
    if (!gr) { notify("Couldn't find a grid reference in that link. Paste the full Grid Reference Finder URL.", "error"); return; }
    const ll = osGridRefToLatLng(gr);
    if (!ll) { notify(`"${gr}" doesn't look like a valid OS grid reference.`, "error"); return; }
    const figures = gridRefFigures(gr);
    // The grid ref comes straight from the link (exact); spread fills the other three.
    await spread({ skip: ["gr"], lat: ll[0], lng: ll[1], dp: figuresToDecimals(figures), figures,
                   busyKey: "link", source: "the link", preset: { [keys.gr]: gr } });
  };

  return { busy, grUrl, setGrUrl, fromW3w, fromGridRef, fromLatLng, fromPostcode,
           fromGrUrl, fromDevice };
}
